import moment from "moment-timezone";

const DAY = "YYYY-MM-DD";

export const PERIODS = ["4w", "3m", "6m", "12m", "custom"];
export const METRICS = ["average", "total", "unique"];
export const HOUR_SETS = { open: [5, 22], all: [0, 23] };
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const WEEKDAYS_LONG = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const PERIOD_DAYS = { "4w": 28, "3m": 84, "6m": 182, "12m": 365 };
const PEAK_SLOTS = 3;

export const defaultBusyFilters = () => ({
  period: "4w",
  customFrom: "",
  customTo: "",
  hours: "open",
  locations: [],
  metric: "average",
});

/** Gym-local day range of a period preset; `custom` uses the picked days. */
export const busyRange = (filters, timeZone, now = new Date()) => {
  const today = moment.tz(now, timeZone).format(DAY);
  if (filters.period === "custom" && filters.customFrom && filters.customTo) {
    return { from: filters.customFrom, to: filters.customTo };
  }
  const days = PERIOD_DAYS[filters.period] ?? PERIOD_DAYS["4w"];
  return {
    from: moment
      .tz(today, DAY, timeZone)
      .subtract(days - 1, "days")
      .format(DAY),
    to: today,
  };
};

/** What the grid shows before FaciliKey has sent a single event. */
export const emptyBusy = (filters, timeZone, now = new Date()) => {
  const [hourFrom, hourTo] = HOUR_SETS[filters.hours] ?? HOUR_SETS.open;
  const { from, to } = busyRange(filters, timeZone, now);
  return {
    metric: filters.metric,
    hourFrom,
    hourTo,
    effectiveFrom: from,
    effectiveTo: to,
    noData: true,
    weekdayCounts: Array(7).fill(0),
    grid: [],
    max: 0,
    peaks: [],
    quietest: [],
    averagePerDay: 0,
    dailyVisits: [],
    locations: [],
  };
};

/** Filters -> query string params of door-busy-times and its export. */
export const busyQuery = (filters, timeZone, now = new Date()) => {
  const [hourFrom, hourTo] = HOUR_SETS[filters.hours] ?? HOUR_SETS.open;
  return {
    ...busyRange(filters, timeZone, now),
    metric: filters.metric,
    hourFrom,
    hourTo,
    ...(filters.locations.length && { locations: filters.locations.join(",") }),
  };
};

export const isBusyFiltered = (filters) =>
  filters.period !== "4w" ||
  filters.hours !== "open" ||
  filters.locations.length > 0;

export const hourRange = (hourFrom, hourTo) =>
  Array.from({ length: hourTo - hourFrom + 1 }, (_, index) => hourFrom + index);

export const hourLabel = (hour) => {
  const suffix = hour < 12 ? "am" : "pm";
  return `${hour % 12 || 12} ${suffix}`;
};

/** "6–7 pm" / "11 am–12 pm": the hour slot that starts at `hour`. */
export const hourSlot = (hour) => {
  const start = hour % 12 || 12;
  const end = (hour + 1) % 12 || 12;
  const startSuffix = hour < 12 ? "am" : "pm";
  const endSuffix = (hour + 1) % 24 < 12 ? "am" : "pm";
  return startSuffix === endSuffix
    ? `${start}–${end} ${endSuffix}`
    : `${start} ${startSuffix}–${end} ${endSuffix}`;
};

export const hourTick = (hour) => hourLabel(hour).replace(" ", "").slice(0, -1);

export const formatMetric = (value, metric) =>
  metric === "average"
    ? value.toFixed(1)
    : Math.round(value).toLocaleString("en-US");

/** Share of the busiest cell, 0 for an empty one; used for the heat colour. */
export const heatPercent = (value, max) =>
  value > 0 && max > 0 ? Math.round(8 + (value / max) * 92) : 0;

/**
 * The grid as 7 rows (Mon-Sun) of cells for the shown hours. A weekday that
 * the range has not seen yet (`weekdayCounts` 0) has no data rather than 0.
 */
export const heatRows = (busy) => {
  const hours = hourRange(busy.hourFrom, busy.hourTo);
  const peaks = new Set(
    busy.peaks.map((cell) => `${cell.weekday}|${cell.hour}`)
  );
  const byKey = new Map(
    busy.grid.map((cell) => [`${cell.weekday}|${cell.hour}`, cell])
  );
  return WEEKDAYS.map((label, index) => {
    const weekday = index + 1;
    const noData = busy.weekdayCounts[index] === 0;
    return {
      weekday,
      label,
      long: WEEKDAYS_LONG[index],
      noData,
      cells: hours.map((hour) => {
        const cell = byKey.get(`${weekday}|${hour}`);
        return {
          hour,
          cell,
          value: cell ? cell[busy.metric] : 0,
          peak: peaks.has(`${weekday}|${hour}`),
          noData,
        };
      }),
    };
  });
};

export const peakRank = (busy, cell) =>
  busy.peaks.findIndex(
    (peak) => peak.weekday === cell.weekday && peak.hour === cell.hour
  ) + 1;

/** Bars of one weekday by hour, scaled to its busiest hour. */
export const dayHourBars = (busy, weekday) => {
  const hours = hourRange(busy.hourFrom, busy.hourTo);
  const cells = hours.map((hour) =>
    busy.grid.find((cell) => cell.weekday === weekday && cell.hour === hour)
  );
  const values = cells.map((cell) => (cell ? cell[busy.metric] : 0));
  const max = Math.max(0, ...values);
  return hours.map((hour, index) => ({
    hour,
    value: values[index],
    ratio: max ? values[index] / max : 0,
  }));
};

export const SLOT_SUMMARY_LIMIT = PEAK_SLOTS;

const niceStep = (max) => {
  if (max <= 10) return 2;
  const raw = max / 4;
  const power = 10 ** Math.floor(Math.log10(raw));
  const unit = [1, 2, 5, 10].find((step) => step * power >= raw) * power;
  return unit;
};

/**
 * Visits-per-day bars. The average ignores today (partial). Axis labels go on
 * Mondays for short ranges and on the 1st of the month for long ones.
 */
export const dailyChart = (dailyVisits, timeZone) => {
  const width = 1240;
  const height = 200;
  const pad = 34;
  const complete = dailyVisits.filter((day) => !day.partial);
  const average = complete.length
    ? complete.reduce((sum, day) => sum + day.visits, 0) / complete.length
    : 0;
  const peak = Math.max(1, ...dailyVisits.map((day) => day.visits));
  const step = niceStep(peak);
  const top = Math.ceil(peak / step) * step;
  const y = (value) => height - 22 - (value / top) * (height - 40);
  const slot = (width - pad) / Math.max(1, dailyVisits.length);
  const byMonth = dailyVisits.length > 60;

  return {
    width,
    height,
    average,
    averageY: y(average),
    ticks: Array.from({ length: top / step + 1 }, (_, index) => {
      const value = index * step;
      return { value, y: y(value) };
    }),
    bars: dailyVisits.map((day, index) => {
      const date = moment.tz(day.date, DAY, timeZone);
      const showLabel = byMonth
        ? date.date() === 1 || index === 0
        : date.isoWeekday() === 1 || index === 0;
      return {
        ...day,
        x: pad + index * slot + slot * 0.18,
        width: Math.max(1, slot * 0.64),
        center: pad + index * slot + slot / 2,
        y: y(day.visits),
        height: height - 22 - y(day.visits),
        label: showLabel ? date.format("MMM D") : null,
        title: date.format("ddd, MMM D"),
      };
    }),
  };
};

const csvList = (value) =>
  (typeof value === "string" ? value : "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

/** Busy times filters from the page's query string (defaults when unknown). */
export const busyFiltersFromQuery = (query = {}) => {
  const period = PERIODS.includes(query.period) ? query.period : "4w";
  const custom = period === "custom";
  const day = (value) =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
  return {
    period,
    customFrom: custom ? day(query.from) : "",
    customTo: custom ? day(query.to) : "",
    hours: query.hours === "all" ? "all" : "open",
    locations: csvList(query.locations),
    metric: METRICS.includes(query.metric) ? query.metric : "average",
  };
};

/** Busy times filters -> body of `/actions/refresh` (empty string removes a key). */
export const busyBody = (filters) => ({
  period: filters.period === "4w" ? "" : filters.period,
  from: filters.period === "custom" ? filters.customFrom : "",
  to: filters.period === "custom" ? filters.customTo : "",
  hours: filters.hours === "open" ? "" : filters.hours,
  locations: filters.locations.join(","),
  metric: filters.metric === "average" ? "" : filters.metric,
});
